import React, { useState, useRef, useEffect } from 'react';
import {
  ChefHat,
  Package,
  Edit2,
  Trash2,
  Eye,
  TrendingUp,
  TrendingDown,
  Clock,
  IndianRupee,
  Copy,
  MoreVertical,
} from 'lucide-react';


const FOOD_COST_THRESHOLD = {
  GOOD: 30, // Below 30% - Healthy (green)
  WARNING: 35, // 30-35% - Needs attention (amber)
};

/**
 * onEdit      — undefined if user lacks EDIT permission   → button hidden
 * onDelete    — undefined if user lacks DELETE permission  → menu item hidden
 * onDuplicate — undefined if user lacks DUPLICATE permission → menu item hidden
 * onView      — always provided (read-only)
 */
export default function RecipeCard({
  recipe,
  onEdit,
  onView,
  onDelete,
  onDuplicate,
}) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const {
    menuItemName,
    categoryName,
    ingredients = [],
    totalCost: rawTotalCost,
    sellingPrice: rawSellingPrice,
    basePrice,  
    updatedAt,
  } = recipe;

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const ingredientCount = ingredients.length;

  const computedTotalCost = ingredients.reduce((sum, ing) => {
    const qty = Number(ing.quantity ?? 0);
    const unitCost = Number(ing.unitCost ?? 0);
    const rowCost =
      typeof ing.totalCost === 'number'
        ? ing.totalCost
        : qty * unitCost;
    return sum + (Number.isNaN(rowCost) ? 0 : rowCost);
  }, 0);

  const safeTotalCost =
    typeof rawTotalCost === 'number' && !Number.isNaN(rawTotalCost) && rawTotalCost > 0
      ? rawTotalCost
      : computedTotalCost;

  const safeSellingPrice =
    typeof rawSellingPrice === 'number' && !Number.isNaN(rawSellingPrice)
      ? rawSellingPrice
      : 0;

  const foodCostPercentage =
    safeSellingPrice > 0 ? (safeTotalCost / safeSellingPrice) * 100 : 0;

  const grossProfit = safeSellingPrice - safeTotalCost;

  const getFoodCostStatus = () => {
    if (foodCostPercentage <= FOOD_COST_THRESHOLD.GOOD) return 'good';
    if (foodCostPercentage <= FOOD_COST_THRESHOLD.WARNING) return 'warning';
    return 'danger';
  };

  const foodCostStatus = getFoodCostStatus();

  const getStatusColors = () => {
    switch (foodCostStatus) {
      case 'good':
        return {
          bg: 'bg-green-50',
          text: 'text-green-700',
          border: 'border-green-200',
          progress: 'bg-green-500',
          badge: 'bg-green-100 text-green-700',
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          text: 'text-amber-700',
          border: 'border-amber-200',
          progress: 'bg-amber-500',
          badge: 'bg-amber-100 text-amber-700',
        };
      default:
        return {
          bg: 'bg-red-50',
          text: 'text-red-700',
          border: 'border-red-200',
          progress: 'bg-red-500',
          badge: 'bg-red-100 text-red-700',
        };
    }
  };

  const colors = getStatusColors();

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatNumber = (value, digits = 2) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '0.00';
    return n.toFixed(digits);
  };

  const ingredientPreview = ingredients.slice(0, 3);
  const remainingCount = ingredients.length - 3;

  // Kebab menu is only rendered when at least one action is available
  const hasKebabItems = !!onDuplicate || !!onDelete;

  return (
    <div className="bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-200 overflow-hidden group">
      {/* Card Header */}
      <div className="relative p-4 pb-3">
        {/* Food Cost Badge */}
        <div
          className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${colors.badge}`}
        >
          {foodCostStatus === 'good' ? (
            <TrendingDown size={12} />
          ) : (
            <TrendingUp size={12} />
          )}
          {formatNumber(foodCostPercentage, 1)}%
        </div>

        {/* Menu Item Info */}
        <div className="flex items-start gap-3 pr-16">
          <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
            <ChefHat size={22} className="text-teal-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 truncate text-base">
              {menuItemName}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{categoryName}</p>
          </div>
        </div>
      </div>

      {/* Ingredients Preview */}
      <div className="px-4 py-3 bg-slate-50 border-y border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <Package size={14} className="text-slate-400" />
          <span className="text-xs font-medium text-slate-600">
            {ingredientCount} Ingredients
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ingredientPreview.map((ing, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-600"
            >
              {ing.name ?? `Item #${ing.idInventoryItem ?? ''}`}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-500">
              +{remainingCount} more
            </span>
          )}
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="p-4 space-y-3">
        {/* Cost Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">
                Cost
              </p>
              <p className="text-sm font-semibold text-slate-700 flex items-center">
                <IndianRupee size={12} className="mr-0.5" />
                {formatNumber(safeTotalCost)}
              </p>
            </div>
            <div className="h-8 w-px bg-slate-200" />
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">
                Price
              </p>
              <p className="text-sm font-medium text-slate-600 flex items-center">
                <IndianRupee size={12} className="mr-0.5" />
                {formatNumber(basePrice)}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">
              Profit
            </p>
            <p
              className={`text-sm font-bold ${
                grossProfit >= 0 ? 'text-green-600' : 'text-red-600'
              } flex items-center justify-end`}
            >
              <IndianRupee size={12} className="mr-0.5" />
              {formatNumber(grossProfit)}
            </p>
          </div>
        </div>

        {/* Food Cost Progress Bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-slate-400 uppercase tracking-wide">
              Food Cost
            </span>
            <span className={`text-xs font-semibold ${colors.text}`}>
              {formatNumber(foodCostPercentage, 1)}%
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${colors.progress}`}
              style={{ width: `${Math.min(foodCostPercentage, 100)}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[9px] text-slate-300">0%</span>
            <span className="text-[9px] text-slate-300">30%</span>
            <span className="text-[9px] text-slate-300">50%</span>
          </div>
        </div>
      </div>

      {/* Card Footer */}
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Clock size={12} />
          <span>Updated {formatDate(updatedAt)}</span>
        </div>
        <div className="flex items-center gap-1">
          {/* View — always visible */}
          <button
            onClick={onView}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
            title="View Recipe"
          >
            <Eye size={16} />
          </button>

          {/* Edit — only if permitted */}
          {onEdit && (
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-100 transition-colors"
              title="Edit Recipe"
            >
              <Edit2 size={16} />
            </button>
          )}

          {/* Kebab Menu — only rendered when at least one action is available */}
          {hasKebabItems && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                title="More actions"
              >
                <MoreVertical size={16} />
              </button>

              {showMenu && (
                <div className="absolute right-0 bottom-full mb-1 w-36 bg-white border border-slate-200 rounded-lg shadow-lg z-10 py-1 overflow-hidden">
                  {/* Duplicate — only if permitted */}
                  {onDuplicate && (
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onDuplicate();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Copy size={14} className="text-blue-500" />
                      Duplicate
                    </button>
                  )}
                  {/* Delete — only if permitted */}
                  {onDelete && (
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onDelete();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}